import { defineStore } from 'pinia';
import axios from 'axios';

export const useMarketsStore = defineStore('markets', {
    state: () => ({
        loading: false,
        items: [],
        orders: [],
        trades: {},
        options: {},
        marketPressure: {},
        seq: 0, // Sequence number for tracking updates
        lastUpdatedAt: null, // Timestamp of last update for stale detection
    }),

    getters: {
        getMarkets: (state) => state.items,
        
        getMarket: (state) => (name) => {
            return state.items.find((market) => market.name === name);
        },
        
        getMarketTrades: (state) => (market) => {
            return state.trades[market] || [];
        },
        
        getOptionsTrades: (state) => (market) => {
            return state.options[market] || [];
        },
        
        getMarketPressure: (state) => (market) => {
            return state.marketPressure ? state.marketPressure[market] : null;
        },
    },

    actions: {
        async fetchMarkets(route) {
            try {
                const response = await axios.get(route);
                this.items = response.data.data;
                this.seq++; // Increment sequence on fetch
                this.lastUpdatedAt = Date.now();
            } catch (error) {
                console.error('Failed to fetch markets:', error);
            }
        },

        updateMarket(payload) {
            if (!payload?.market?.name) return;
            const index = this.items.findIndex(item => item.name === payload.market.name);
            if (index > -1) {
                this.items[index] = { ...this.items[index], ...payload.market };
            } else {
                this.items.push(payload.market);
            }
            this.seq++;
        },

        updateMarketStats(payload) {
            const index = this.items.findIndex(item => item.name === payload.market.name);
            
            if (index > -1) {
                const tempMarket = { ...this.items[index] };
                tempMarket.prevlast = tempMarket.last;
                tempMarket.last = payload.market.last;
                tempMarket.low = payload.market.low;
                tempMarket.high = payload.market.high;
                tempMarket.volume = payload.market.volume;
                tempMarket.change = payload.market.change;
                
                this.items[index] = tempMarket;
                this.seq++; // Increment sequence on update
                this.lastUpdatedAt = Date.now(); // Track update time for stale detection

                // Emit event for market watchers
                if (window.VueWorker) {
                    window.VueWorker.emit("updateMarketWorker", {
                        'name': payload.market.name,
                        'last': payload.market.last,
                        'prevlast': tempMarket.prevlast
                    });
                }
            }
        },

        setMarketTrades(payload) {
            this.trades[payload.market] = payload.trades;
        },

        setOptionsTrades(market, responseData) {
            const list = responseData?.data ?? responseData;
            this.options[market] = Array.isArray(list) ? list : [];
        },

        /** Prepend one settled option to trades list (real-time from OptionsStatusUpdated). */
        prependOptionTrade(market, tradePayload) {
            if (!market || !tradePayload) return;
            if (!this.options[market]) {
                this.options[market] = [];
            }
            this.options[market].unshift(tradePayload);
            if (this.options[market].length > 100) {
                this.options[market] = this.options[market].slice(0, 100);
            }
        },

        async fetchMarketTrades({ market, route }) {
            try {
                const response = await axios.get(route, {
                    params: { market }
                });
                this.trades[market] = response.data.data;
            } catch (error) {
                console.error('Failed to fetch market trades:', error);
            }
        },

        async fetchOptionsTrades({ market, route }) {
            try {
                const response = await axios.get(route, {
                    params: { market }
                });
                const raw = response.data?.data ?? response.data;
                this.options[market] = Array.isArray(raw) ? raw : [];
            } catch (error) {
                console.error('Failed to fetch options trades:', error);
            }
        },

        updateMarketTrade(payload) {
            const market = payload.market.name;
            if (!this.trades[market]) {
                this.trades[market] = [];
            }
            this.trades[market].unshift(payload.trade);
            
            // Limit trades to prevent memory issues
            if (this.trades[market].length > 100) {
                this.trades[market] = this.trades[market].slice(0, 100);
            }
        },

        updateMarketTradeLite(payload) {
            const market = payload.market.name;
            if (!this.trades[market]) {
                this.trades[market] = [];
            }
            this.trades[market].unshift(payload.trade);
            
            // Limit trades to prevent memory issues
            if (this.trades[market].length > 100) {
                this.trades[market] = this.trades[market].slice(0, 100);
            }
        },

        marketTradePressure(payload) {
            this.marketPressure[payload.market] = {
                buy: payload.buy,
                sell: payload.sell,
                timestamp: new Date().toISOString()
            };
        }
    }
});



